import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/axiosInstance";
import Env from "../../runtimeEnv";
import { isNativeMobileShell } from "../../native/mobileBridge";

const SPACE_NAMES = {
  beach: "Beach Office",
  mountain: "Forest Office",
  park: "Quiet Park",
  campsite: "Campfire",
  "winter-holiday": "Winter Cabin",
};

function invitationFromMeeting(meeting, actorID, currentMeetingID) {
  const room = meeting.meetingID?.split(".")[0]?.replace(/-therapy$/, "");
  if (!SPACE_NAMES[room] || meeting.meetingID === currentMeetingID) return null;
  return {
    key: `${meeting.meetingID}:${actorID}:${meeting.joinedAtByUser?.[actorID] || "current"}`,
    meetingID: meeting.meetingID,
    actorID,
    room,
    spaceName: SPACE_NAMES[room],
    device: meeting.joinDeviceByUser?.[actorID] || "",
    fallbackName: actorID === meeting.therapistUserID ? "Your therapist" : "Your client",
  };
}

export default function MeetingJoinInvitation({ userID, enabled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [names, setNames] = useState({});
  const nameRequests = useRef(new Set());
  const currentMeetingID = location.pathname.startsWith("/unity-room/")
    ? sessionStorage.getItem("meetingID") : null;

  useEffect(() => {
    if (!currentMeetingID) return;
    setDismissed((prior) => {
      const next = new Set(prior);
      invitations.filter((item) => item.meetingID === currentMeetingID)
        .forEach((item) => next.add(item.key));
      return next;
    });
  }, [currentMeetingID, invitations]);

  useEffect(() => {
    if (!enabled || !userID || isNativeMobileShell()) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const response = await axios.get(`${Env.REACT_APP_PYTHON_API_URL}/api/GetMeetings/${userID}`, {
          params: { t: Date.now() },
        });
        if (!active) return;
        const found = response.data.flatMap((meeting) =>
          Object.entries(meeting.onlineByUser || {})
            .filter(([actorID, online]) => actorID !== userID && online === true)
            .map(([actorID]) => invitationFromMeeting(meeting, actorID, currentMeetingID))
            .filter(Boolean)
        );
        setInvitations((prior) => {
          const seen = new Set(prior.map((item) => item.key));
          return [...prior, ...found.filter((item) => !seen.has(item.key))];
        });
      } catch (error) {
        // Keep the current invitation visible through a temporary refresh error.
      }
    };
    poll();
    const timer = setInterval(poll, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [enabled, userID, currentMeetingID]);

  const invitation = invitations.find((item) => !dismissed.has(item.key) && item.meetingID !== currentMeetingID);

  useEffect(() => {
    if (!invitation || nameRequests.current.has(invitation.actorID)) return;
    nameRequests.current.add(invitation.actorID);
    axios.get(`${Env.REACT_APP_PYTHON_API_URL}/api/meeting-participant-name/${userID}`, {
      params: { meetingID: invitation.meetingID, participantID: invitation.actorID },
    }).then((response) => {
      const name = response.data?.name?.trim();
      if (name) setNames((prior) => ({ ...prior, [invitation.actorID]: name }));
    }).catch(() => {});
  }, [invitation, userID]);

  if (!enabled || isNativeMobileShell() || !invitation) return null;

  const inAnotherSpace = location.pathname.startsWith("/unity-room/");
  const actorName = names[invitation.actorID] || invitation.fallbackName;
  const joinedOnMobile = invitation.device === "mobile";

  const join = async () => {
    if (inAnotherSpace && currentMeetingID && currentMeetingID !== invitation.meetingID) {
      try {
        await axios.post(`${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${userID}`, {
          meetingID: currentMeetingID,
          online: false,
        });
      } catch (error) { /* The previous presence expires on its own. */ }
    }
    sessionStorage.setItem("meetingID", invitation.meetingID);
    sessionStorage.setItem("userId", userID);
    setDismissed((prior) => new Set(prior).add(invitation.key));
    navigate(`/unity-room/${invitation.room}`, { state: { invitationJoin: Date.now() } });
  };

  return (
    <aside role="dialog" aria-label="Join your session" className="fixed bottom-5 right-5 z-[100] w-[min(360px,calc(100vw-40px))] rounded-2xl border border-sky-200 bg-white p-5 text-slate-900 shadow-2xl">
      <button
        type="button"
        aria-label="Dismiss invitation"
        onClick={() => setDismissed((prior) => new Set(prior).add(invitation.key))}
        className="absolute right-3 top-2 text-2xl text-slate-500 hover:text-slate-900"
      >×</button>
      <p className="pr-6 text-base font-semibold">Join your session?</p>
      <p className="mt-2 text-sm leading-6">
        {actorName} has joined {inAnotherSpace ? "another space: " : "the "}
        <strong>{invitation.spaceName}</strong>
        {joinedOnMobile ? " on their mobile device" : ""}. Would you like to join them?
      </p>
      <button type="button" onClick={join} className="mt-4 rounded-full bg-sky-500 px-5 py-2 font-semibold text-white hover:bg-sky-600">
        Join
      </button>
    </aside>
  );
}
